import React, { useEffect, useMemo, useRef, useState } from "react";
import "./bill-duplicate.style.scss";
import { useNavigate } from "react-router-dom";

// form handle
import { useFormik } from "formik";
// import * as yup from "yup";

import { formatEnDate } from "../../global/date.util";

// global functions
import { isEmptyObject } from "../../global/function";
import {
	isChargeModeActive,
	computeChargeAmounts,
} from "../../global/billing-charge.util";

// components
import InputDatePicker from "../input-date-picker/input-date-picker.component";
import EmptyRecord from "../empty-record/empty-record.component";
import FormError from "../form-error/form-error.component";
import SuccessDisplay from "../success-display/success-display.component";
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import { Button } from "../ui/button";
import { FaCheck, FaCopy } from "react-icons/fa6";

// queries
import { useCompanyDetails } from "../../queries/company.queries";
import { useSaveDraft, DraftPayload } from "../../queries/drafts.queries";
import { useBillsForSelection } from "../../queries/bill.queries";
import type { Bill, BillSearchFilters } from "../../queries/bill.queries";

interface BillDuplicateProps {
	isOpen: boolean;
	onDuplicateModalClose: () => void;
	filterValues: BillSearchFilters;
}

// Partial<Bill>, not Bill: handleBillsDuplicate strips datetimeClient/
// createdAt/updatedAt/fiscal_year/invoice_number before submission (a
// duplicated bill gets fresh values for all of these server-side), which
// needs those fields to type as optional-and-deletable.
type DuplicableBill = Partial<Bill>;

function BillDuplicate({
	isOpen,
	onDuplicateModalClose,
	filterValues,
}: BillDuplicateProps) {
	const { data: billsData, isLoading: billsLoading } = useBillsForSelection(filterValues);
	const reduxBillList = useMemo(() => billsData?.bill ?? [], [billsData]);

	const { data: companyData } = useCompanyDetails();
	const preferenceDetails = companyData?.company?.preference;

	const saveDraftMutation = useSaveDraft();
	const isLoading = saveDraftMutation.isPending;

	// Brief SuccessDisplay before navigating away — same pattern as
	// customer-bulk-draft's post-useSaveDraft fix, replacing the toast that
	// had nothing left to show once navigation happened on the same tick.
	const navigateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	useEffect(() => {
		return () => {
			if (navigateTimeoutRef.current) clearTimeout(navigateTimeoutRef.current);
		};
	}, []);

	const [billsToDuplicate, setBillsToDuplicate] = useState<
		Record<number, DuplicableBill>
	>({});

	const navigate = useNavigate();

	// fetch last bill date
	// useEffect(() => {
	// 	const fetchLastBillDate = async () => {
	// 		await dispatch(getBillDates());
	// 	};
	// 	fetchLastBillDate();
	// }, [dispatch]);

	const billList = useMemo(() => {
		return reduxBillList.filter((bill) => !bill.bill_canceled);
	}, [reduxBillList]);

	const billFilter = useMemo(() => {
		let billVoid;
		if (filterValues?.bill_void === true) {
			billVoid = "Voided Bills";
		} else if (filterValues?.bill_void === false) {
			billVoid = "Non-void Bills";
		} else {
			billVoid = "";
		}

		let filterObject: Record<string, unknown> = {
			...filterValues,
			bill_void: billVoid,
		};

		// Remove empty string properties
		for (const key in filterObject) {
			if (filterObject[key] === "") {
				delete filterObject[key];
			}
		}

		// Handle total_sales_min and total_sales_max condition
		if (filterObject.total_sales_max === 0) {
			delete filterObject.total_sales_min;
			delete filterObject.total_sales_max;
		}
		return { ...filterObject };
	}, [filterValues]);

	// const invoiceDateSchema = () => {
	// 	return yup.object().shape({
	// 		invoice_date: yup
	// 			.string()
	// 			.required("Invoice Date is required")
	// 			.test(
	// 				"is-later-than-last-bill-date",
	// 				"Date must be later than the last bill date",
	// 				function (value) {
	// 					if (lastBillDate !== "") {
	// 						const draft_date = new Date(value);
	// 						const last_bill_date = new Date(lastBillDate);

	// 						if (draft_date >= last_bill_date) {
	// 							return true; // Date is later than the global date
	// 						} else {
	// 							return false; // Date is not later than the global date
	// 						}
	// 					} else {
	// 						return true;
	// 					}
	// 				}
	// 			)
	// 			.test(
	// 				"is-not-more-than-1-month-later",
	// 				"Date should not exceed 1 month from today",
	// 				function (value) {
	// 					const draft_date = new Date(value);
	// 					const currentDate = new Date();
	// 					const maxDate = new Date();
	// 					maxDate.setMonth(currentDate.getMonth() + 1); // Set max date to 1 month later

	// 					if (draft_date <= maxDate) {
	// 						return true; // Date is valid
	// 					}

	// 					return false; // Date exceeds 1 month from today
	// 				}
	// 			),
	// 	});
	// };

	const formik = useFormik({
		// validationSchema: invoiceDateSchema,
		initialValues: {
			sales_date: formatEnDate(new Date(), "YYYY-MM-DD"),
		},
		// Real submit handling lives in handleBillsDuplicate below —
		// formik.submitForm() here is only ever used to surface validation
		// errors (see the invalid-form branch), never to actually submit.
		onSubmit: () => {},
	});


	const handleBillsDuplicate = async () => {
		if (!isLoading) {
			const formError = await formik.validateForm();
			if (isEmptyObject(formError) && !isEmptyObject(billsToDuplicate)) {
				const billArray: DraftPayload[] = Object.values(
					billsToDuplicate
				).map((bill) => {
					let bill_temp: DuplicableBill = { ...bill };

					// Calculate initial totals. The original bill's chargeable
					// base lives in taxable_sales_vat (VAT-mode bill) or
					// amount_for_esf (ESF-mode bill) — VAT/ESF are either/or so
					// only one is ever populated, same reused-field convention
					// as invoice-form.component.jsx's calculateWithTax.
					let totalSales = {
						taxable:
							(bill?.taxable_sales_vat || bill?.amount_for_esf || 0) +
							(bill?.taxable_discount || 0) -
							(bill?.taxable_service_charge || 0),
						non_taxable:
							(bill?.non_taxable_amt || 0) +
							(bill?.non_taxable_discount || 0) -
							(bill?.non_taxable_service_charge || 0),
					};

					// Adjust discount and service charge based on preferences
					if (!preferenceDetails?.discount) {
						bill_temp.taxable_discount = 0;
						bill_temp.non_taxable_discount = 0;
					}

					if (
						!preferenceDetails?.service_charge ||
						preferenceDetails?.service_charge <= 0
					) {
						bill_temp.taxable_service_charge = 0;
						bill_temp.non_taxable_service_charge = 0;
					}

					if (isChargeModeActive(preferenceDetails)) {
						// Chargeable bucket carries forward, VAT or ESF applied
						// depending on which mode is currently active — not
						// necessarily the mode the original bill was created in.
						const chargeableBase =
							totalSales.taxable -
							(bill_temp.taxable_discount || 0) +
							(bill_temp.taxable_service_charge || 0);

						const chargeAmounts = computeChargeAmounts(
							chargeableBase,
							preferenceDetails
						);

						bill_temp.taxable_sales_vat = chargeAmounts.taxable_sales_vat;
						bill_temp.vat = chargeAmounts.vat;
						bill_temp.amount_for_esf = chargeAmounts.amount_for_esf;
						bill_temp.esf = chargeAmounts.esf;

						bill_temp.non_taxable_amt =
							totalSales.non_taxable -
							(bill_temp.non_taxable_discount || 0) +
							(bill_temp.non_taxable_service_charge || 0);

						// Ensure items are correctly marked as vatable. Legacy
						// bills predating the per-item flag may be missing it —
						// if the whole bill is in the chargeable bucket, that's
						// unambiguous regardless of which charge mode is active.
						if ((bill_temp.non_taxable_amt || 0) <= 0) {
							bill_temp.billed_items = (bill_temp.billed_items ?? []).map(
								(item) => ({
									...item,
									vatable: item.vatable ?? true,
								})
							);
						}
					} else {
						// Neither VAT nor ESF active: move all chargeable totals
						// to non-taxable
						const totalChargeable =
							totalSales.taxable -
							(bill_temp.taxable_discount || 0) +
							(bill_temp.taxable_service_charge || 0);

						bill_temp.taxable_sales_vat = 0;
						bill_temp.taxable_discount = 0;
						bill_temp.taxable_service_charge = 0;
						bill_temp.amount_for_esf = 0;
						bill_temp.vat = 0;
						bill_temp.esf = 0;

						bill_temp.non_taxable_amt =
							totalSales.non_taxable -
							(bill_temp.non_taxable_discount || 0) +
							(bill_temp.non_taxable_service_charge || 0) +
							totalChargeable;

						bill_temp.non_taxable_discount = 0;
						bill_temp.non_taxable_service_charge = 0;

						// Mark all items as non-chargeable
						bill_temp.billed_items = (bill_temp.billed_items ?? []).map(
							(item) => ({
								...item,
								vatable: false,
							})
						);
					}

					// Total sales — one formula, correct in all three modes
					// since exactly one of {taxable_sales_vat+vat,
					// amount_for_esf+esf} is ever nonzero (see
					// project-esf-dormant-charge-field memory).
					bill_temp.total_sales =
						(bill_temp.taxable_sales_vat || 0) +
						(bill_temp.non_taxable_amt || 0) +
						(bill_temp.amount_for_esf || 0) +
						(bill_temp.vat || 0) +
						(bill_temp.esf || 0);

					// Set sales and invoice date
					bill_temp.sales_date = formik.values.sales_date;
					bill_temp.invoice_date = formatEnDate(new Date(), "YYYY-MM-DD");

					// Remove unnecessary fields
					delete bill_temp.datetimeClient;
					delete bill_temp.createdAt;
					delete bill_temp.updatedAt;
					delete bill_temp.fiscal_year;
					delete bill_temp.invoice_number;

					return bill_temp as DraftPayload;
				});

				// Save drafts
				try {
					await saveDraftMutation.mutateAsync({
						drafts: billArray,
						draft_bill_status: "draft",
						duplicateBill: true,
						invoice_date: formatEnDate(new Date(), "YYYY-MM-DD"),
					});
					navigateTimeoutRef.current = setTimeout(() => {
						navigate("/invoice/create");
					}, 1200);
				} catch {
					// FormError (bound to saveDraftMutation) renders the message
				}
			} else {
				formik.submitForm();
			}
		}
	};

	const handleBillSelection = (bill: DuplicableBill, index: number) => {
		let state = { ...billsToDuplicate };
		if (state[index]) {
			delete state[index];
		} else {
			let selectedBill: DuplicableBill;
			if (bill?.bill_void) {
				selectedBill = {
					...bill,
					billed_items: bill?.credit_note?.billed_items || [],
				};
			} else {
				selectedBill = { ...bill };
			}
			state[index] = selectedBill;
		}
		setBillsToDuplicate(state);
	};

	const handleToggleAll = () => {
		let objState: Record<number, DuplicableBill>;
		if (Object.keys(billsToDuplicate).length === billList.length) {
			objState = {};
		} else {
			objState = billList.reduce(
				(acc: Record<number, DuplicableBill>, value, index) => {
					if (value?.bill_void) {
						acc[index] = {
							...value,
							billed_items: value?.credit_note?.billed_items || [],
						};
					} else {
						acc[index] = value;
					}

					return acc;
				},
				{}
			);
		}
		setBillsToDuplicate(objState);
	};

	return (
		<StructuredSideModal
			open={isOpen}
			onOpenChange={(open) => { if (!open) onDuplicateModalClose(); }}
			title="Duplicate Bills"
			width={560}
			isDirty={!saveDraftMutation.isSuccess && (formik.dirty || !isEmptyObject(billsToDuplicate))}
			footer={!saveDraftMutation.isSuccess && (
				<div className="flex justify-end">
					<Button
						size="sm"
						onClick={handleBillsDuplicate}
						disabled={isEmptyObject(billsToDuplicate) || isLoading}
					>
						<FaCopy /> Duplicate to draft
					</Button>
				</div>
			)}
		>
		<div className="bill-duplicate-container">
			{saveDraftMutation.isSuccess ? (
				<SuccessDisplay
					title="Draft(s) created"
					description="Selected bills have been duplicated to drafts."
				/>
			) : (
			<div className="modal-body">
				<FormError
					isError={saveDraftMutation.isError}
					error={saveDraftMutation.error}
					fallback="Something went wrong. Please try again."
				/>
				<div className="bill-filters-wrapper">
					<h2 className="section-header">Filter Values</h2>
					<div className="bill-filter-container">
						{Object.values(billFilter).map((value, i) => (
							<div
								key={i}
								className="bill-filter-container-inner"
							>
								<span>{String(value)}</span>
							</div>
						))}
					</div>
				</div>
				<div className="divider" />

				<div className="bills-wrapper">
					<p className="section-description">
						Choose Sales date to duplicate
					</p>
					<div className="input-item">
						<p className="input-label">Sales Date</p>
						<InputDatePicker
							value={
								formik.values.sales_date
									? new Date(formik.values.sales_date)
									: null
							}
							onChange={(result) =>
								formik.setFieldValue(
									"sales_date",
									formatEnDate(result.en, "YYYY-MM-DD"),
								)
							}
							maxDate={new Date()}
							clearable
							onClear={() => formik.setFieldValue("sales_date", "")}
							error={
								formik.submitCount > 0
									? formik.errors?.sales_date
									: undefined
							}
						/>
					</div>
				</div>
				<div className="divider" />

				{Array.isArray(billList) && billList.length > 0 ? (
					<div className="bills-wrapper">
						<h2 className="section-header">Filtered Bills</h2>
						<p className="section-description">
							Select all or some of the filtered bills to
							duplicate.
						</p>
						<div
							className="bill-container"
							onClick={handleToggleAll}
						>
							<div
								className={`bill-select ${
									Object.keys(billsToDuplicate).length ===
										billList.length && "selected"
								}`}
							>
								{Object.keys(billsToDuplicate).length ===
									billList.length && (
									<FaCheck
										style={{
											color: "#46BE46",
											fontSize: "12px",
										}}
									/>
								)}
							</div>
							<div className="select-btn">
								{Object.keys(billsToDuplicate).length ===
								billList.length
									? "Deselect All"
									: "Select All"}
							</div>
						</div>
						{billList.map((bill, i) => (
							<div
								className="bill-container"
								key={i}
								onClick={() => handleBillSelection(bill, i)}
							>
								<div
									className={`bill-select ${
										billsToDuplicate[i] && "selected"
									}`}
								>
									{billsToDuplicate[i] && (
										<FaCheck
											style={{
												color: "#46BE46",
												fontSize: "12px",
											}}
										/>
									)}
								</div>

								<div className="bill-container-inner">
									<div className="bill-details">
										<h4 className="bill-client">
											{bill?.buyer_name}
										</h4>
										<div
											className={`bill-status ${
												bill?.bill_void
													? "credited"
													: "bill"
											}`}
										>
											{bill?.bill_void
												? "Credit Note"
												: "Bill"}
										</div>
									</div>
									<div className="bill-details">
										{/* totalSales */}
										Rs. {bill?.total_sales?.toFixed(2)}
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<EmptyRecord caption={billsLoading ? "Loading bills..." : undefined} />
				)}
			</div>
			)}
		</div>
		</StructuredSideModal>
	);
}

export default BillDuplicate;
